import React, { FC } from 'react';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import styled from 'styled-components';
import { stringFormat, T } from 'src/shared/helpers';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
// NON È L'AI (utente, 2026-10-20): genera opzioni da regole, quindi il segno è un'aggiunta di più voci, non la stellina.
import GenerateIcon from 'src/shared/material_icons/av/library_add.svg';
import ColorIcon from 'src/shared/material_icons/image/palette.svg';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import { ComposerAttributeNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';
import { panel, PropertiesPanelLookProvider, PropertiesPanelSwatch } from '../../scene-properties-panel';
import { readMaterialPreview } from '../../scene-editors/materialPreview';
import { ComposerChoice, ComposerMaterialSlots, ComposerObjectSlots, ComposerSlot, ComposerSlotOption } from '../composerSlots';
import { COMPOSER_LABEL_COLUMN } from '../composerWorkspace';
import { softAccentButton } from '../../editorPalette';
import { createComposerAction } from './composerActionEditor';
import { ComposerAttributeNode, ComposerMaterialsColorType, ComposerOptionActionNode, ComposerOptionActionsType } from './composerAttributesModels';

// GENERATE OPTIONS (prototipo, Generate choices; utente 2026-10-20): le opzioni di un attributo in un colpo, ognuna
// con la sua azione. Show / Hide fa due opzioni, On e Off; Materials una per materiale, che lo mette sugli oggetti;
// Colors una per colore, che ricolora i materiali. Il prototipo agisce sugli «oggetti della feature», un dato che il
// prodotto non ha (gli oggetti dell'attributo sono il trigger della camera): gli oggetti si scelgono qui.

type GenerateKind = 'visibility' | 'material' | 'color';

const COLORS: [string, string][] = [
    ['#1E2A38', 'Midnight'],
    ['#3F4750', 'Graphite'],
    ['#5C6672', 'Slate'],
    ['#8892A0', 'Gray'],
    ['#C9CDD2', 'Silver'],
    ['#E9EAEC', 'White'],
    ['#C8B08A', 'Sand'],
    ['#8A6F4E', 'Walnut'],
    ['#B4241F', 'Red'],
    ['#E8650A', 'Orange'],
    ['#B4552F', 'Rust'],
    ['#E6167F', 'Magenta'],
    ['#2E7D46', 'Green'],
    ['#0E7C7C', 'Teal'],
    ['#1B6FD4', 'Blue'],
    ['#7A5AF8', 'Violet']
];

const COLOR_TYPES: ComposerSlotOption[] = Object.entries(ComposerMaterialsColorType).map(([label, value]) => ({ value, label }));

const Bar = styled.div`
    display: flex;
    flex: 0 0 auto;
    padding: 8px 8px 0;
`;

// IL COMANDO PRINCIPALE DELL'ATTRIBUTO (ricetta «principale»: arancio tenue, 28px dentro i pannelli), a tutta larghezza.
const GenerateButton = styled.button`
    ${softAccentButton}
    display: flex;
    width: 100%;
    height: 28px;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border-radius: 8px;
    font: inherit;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;

    svg {
        width: 15px;
        height: 15px;
        fill: currentColor;
    }
`;

const Panel = styled.div`
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    gap: 8px;
    margin: 6px 8px 0;
    padding: 8px;
    border-radius: 10px;
    background: var(--editor-card, #f9fafb);
`;

const Row = styled.div`
    display: grid;
    min-width: 0;
    grid-template-columns: ${COMPOSER_LABEL_COLUMN} minmax(0, 1fr);
    align-items: start;
    gap: 10px;

    > span:first-child {
        padding-top: 6px;
        color: var(--composer-fg, #263238);
        font-size: 12px;
        font-weight: 600;
    }

    > div {
        min-width: 0;
    }
`;

const Swatches = styled.div`
    display: grid;
    grid-template-columns: repeat(8, minmax(0, 1fr));
    gap: 4px;
`;

const Swatch = styled.button<{ $color: string }>`
    aspect-ratio: 1;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: ${(props) => props.$color};
    box-shadow: inset 0 0 0 1px rgb(0 0 0 / 10%);
    cursor: pointer;

    &[aria-pressed='true'] {
        box-shadow:
            0 0 0 2px var(--editor-card, #f9fafb),
            0 0 0 4px var(--editor-accent, #e8650a);
    }
`;

const HexRow = styled.div`
    display: flex;
    gap: 6px;
    margin-top: 6px;

    input {
        min-width: 0;
        flex: 1;
        height: 24px;
        padding: 2px 8px;
        border: 1px solid var(--editor-line, #dbdde1);
        border-radius: 4px;
        background: var(--editor-field, #fff);
        font: inherit;
        font-size: 12px;
    }
`;

const SmallButton = styled.button`
    height: 24px;
    padding: 0 8px;
    border: 1px solid var(--editor-line, #dbdde1);
    border-radius: 4px;
    background: var(--editor-field, #fff);
    color: var(--editor-fg, #262a30);
    font: inherit;
    font-size: 12px;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

const Foot = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--editor-line-soft, #e6e8eb);

    > span {
        flex: 1;
        color: var(--editor-faint, #767a7f);
        font-size: 11px;
    }
`;

// GENERATE NELL'ARANCIO TENUE, come ogni azione principale degli editor (utente, 2026-10-20).
const Make = styled.button`
    ${softAccentButton}
    display: flex;
    height: 28px;
    align-items: center;
    gap: 6px;
    padding: 0 12px;
    border-radius: 8px;
    font: inherit;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: default;
    }

    svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }
`;

const isHex = (text: string) => /^#?[0-9a-f]{6}$/i.test(text.trim());
const normalHex = (text: string) => `#${text.trim().replace('#', '').toUpperCase()}`;

export const ComposerGenerateOptions: FC<{ attribute: ComposerAttributeNode; meshes: BABYLON.AbstractMesh[]; materials: BABYLON.Material[] }> = ({
    attribute,
    meshes,
    materials
}) => {
    const [updateOption, validateAttributes] = useComposerStore((store) => [store.updateOption, store.validateAttributes]);
    const sceneEditor = usePreview3DStore((store) => store.sceneEditor);
    const [open, setOpen] = React.useState(false);
    const [kind, setKind] = React.useState<GenerateKind>('material');
    const [objects, setObjects] = React.useState<string[]>([]);
    const [pickedMaterials, setPickedMaterials] = React.useState<string[]>([]);
    const [targetMaterials, setTargetMaterials] = React.useState<string[]>([]);
    const [colorType, setColorType] = React.useState<string>('');
    const [colors, setColors] = React.useState<string[]>([]);
    const [hex, setHex] = React.useState('');

    React.useEffect(() => setOpen(false), [attribute.id]);

    const meshOptions: ComposerSlotOption[] = meshes.map((mesh) => ({ value: mesh.id, label: mesh.name || mesh.id }));
    const materialOptions: ComposerSlotOption[] = materials.map((material) => ({
        value: material.id,
        label: material.name || material.id,
        preview: <PropertiesPanelSwatch $url={readMaterialPreview(sceneEditor, material.id) ?? undefined} $tint={panel.kindAsset} $size={24} />
    }));
    const materialName = (id: string) => materials.find((material) => material.id === id)?.name || id;
    // IL TIPO DI COLORE SEGUE IL MATERIALE: albedo su un PBR, diffuse sugli altri; si può cambiare.
    const autoColorType = (() => {
        const first = materials.find((material) => material.id === targetMaterials[0]);
        return first?.getClassName?.() === 'PBRMaterial' ? ComposerMaterialsColorType.Albedo : ComposerMaterialsColorType.Diffuse;
    })();
    const effectiveColorType = colorType || autoColorType;

    const count = kind === 'visibility' ? 2 : kind === 'material' ? pickedMaterials.length : colors.length;
    const ready = kind === 'color' ? targetMaterials.length > 0 && colors.length > 0 : objects.length > 0 && count > 0;
    const summary = !ready
        ? kind === 'visibility'
            ? T._('Choose the objects to show and hide', 'Admin')
            : kind === 'material'
              ? T._('Choose the objects and at least one material', 'Admin')
              : T._('Choose the materials and at least one color', 'Admin')
        : stringFormat(count === 1 ? T._('1 option', 'Admin') : T._('{0} options', 'Admin'), count.toString());

    const generate = () => {
        if (!ready) return;
        const start = attribute.options.length;
        const recipes: { name: string; actions: (optionID: string, order: number) => ComposerOptionActionNode[] }[] =
            kind === 'visibility'
                ? [
                      { name: T._('On', 'Admin'), type: ComposerOptionActionsType.SHOW_OBJECTS },
                      { name: T._('Off', 'Admin'), type: ComposerOptionActionsType.HIDE_OBJECTS }
                  ].map((recipe) => ({
                      name: recipe.name,
                      actions: (optionID) => [{ ...createComposerAction(attribute.id, optionID, recipe.type, 0), selector: objects.join(';') }]
                  }))
                : kind === 'material'
                  ? pickedMaterials.map((materialID) => ({
                        name: materialName(materialID),
                        actions: (optionID) => [
                            {
                                ...createComposerAction(attribute.id, optionID, ComposerOptionActionsType.SET_OBJECT_MATERIAL, 0),
                                selector: objects.join(';'),
                                value: materialID
                            }
                        ]
                    }))
                  : colors.map((color) => ({
                        name: COLORS.find(([value]) => value === color)?.[1] ?? color,
                        actions: (optionID) => [
                            {
                                ...createComposerAction(attribute.id, optionID, ComposerOptionActionsType.SET_MATERIAL_COLOR, 0),
                                selector: targetMaterials.join(';'),
                                property: effectiveColorType,
                                value: color
                            }
                        ]
                    }));
        recipes.forEach((recipe, index) => {
            const displayOrder = start + index;
            const node = ComposerAttributeNodeAdapter.toNodeOption(attribute.id, ComposerAttributeNodeAdapter.createNewOption(displayOrder));
            updateOption({
                ...node,
                name: recipe.name,
                description: '',
                attributeID: attribute.id,
                displayOrder,
                isDefault: start === 0 && index === 0,
                isNew: true,
                actions: recipe.actions(node.id, 0)
            });
        });
        void validateAttributes();
        setOpen(false);
        setPickedMaterials([]);
        setColors([]);
    };

    const toggleColor = (color: string) => setColors((current) => (current.includes(color) ? current.filter((value) => value !== color) : [...current, color]));

    return (
        <PropertiesPanelLookProvider value='scene'>
            <Bar>
                <GenerateButton type='button' aria-expanded={open} onClick={() => setOpen(!open)}>
                    <GenerateIcon />
                    {T._('Generate options', 'Admin')}
                </GenerateButton>
            </Bar>
            {open && (
                <Panel>
                    <ComposerChoice
                        ariaLabel={T._('What the options change', 'Admin')}
                        options={[
                            { value: 'visibility', label: T._('Show / Hide', 'Admin') },
                            { value: 'material', label: T._('Materials', 'Admin') },
                            { value: 'color', label: T._('Colors', 'Admin') }
                        ]}
                        value={kind}
                        onChange={(next) => setKind(next as GenerateKind)}
                    />
                    {kind !== 'color' && (
                        <Row>
                            <span>{T._('Objects', 'Admin')}</span>
                            <div>
                                <ComposerObjectSlots
                                    id={`composer-generate-${attribute.id}-objects`}
                                    value={objects}
                                    options={meshOptions}
                                    allowPatterns
                                    onChange={setObjects}
                                />
                            </div>
                        </Row>
                    )}
                    {kind === 'material' && (
                        <Row>
                            <span>{T._('Materials', 'Admin')}</span>
                            <div>
                                <ComposerMaterialSlots
                                    id={`composer-generate-${attribute.id}-materials`}
                                    value={pickedMaterials}
                                    options={materialOptions}
                                    onChange={setPickedMaterials}
                                />
                            </div>
                        </Row>
                    )}
                    {kind === 'color' && (
                        <>
                            <Row>
                                <span>{T._('Materials', 'Admin')}</span>
                                <div>
                                    <ComposerMaterialSlots
                                        id={`composer-generate-${attribute.id}-targets`}
                                        value={targetMaterials}
                                        options={materialOptions}
                                        allowPatterns
                                        onChange={setTargetMaterials}
                                    />
                                </div>
                            </Row>
                            <Row>
                                <span>{T._('Color type', 'Admin')}</span>
                                <div>
                                    <ComposerSlot
                                        id={`composer-generate-${attribute.id}-color-type`}
                                        value={effectiveColorType}
                                        options={COLOR_TYPES}
                                        glyph={<ColorIcon />}
                                        caption={T._('Color type', 'Admin')}
                                        emptyLabel={T._('Choose a color type', 'Admin')}
                                        onChange={setColorType}
                                    />
                                </div>
                            </Row>
                            <Row>
                                <span>{T._('Colors', 'Admin')}</span>
                                <div>
                                    <Swatches>
                                        {/* Il nome del colore nel tooltip degli editor, non in quello del browser. */}
                                        {COLORS.map(([color, name]) => (
                                            <ZTooltip key={color} title={name} position='top'>
                                                <Swatch
                                                    type='button'
                                                    $color={color}
                                                    aria-pressed={colors.includes(color)}
                                                    aria-label={name}
                                                    onClick={() => toggleColor(color)}
                                                />
                                            </ZTooltip>
                                        ))}
                                        {colors
                                            .filter((color) => !COLORS.some(([value]) => value === color))
                                            .map((color) => (
                                                <ZTooltip key={color} title={color} position='top'>
                                                    <Swatch type='button' $color={color} aria-pressed aria-label={color} onClick={() => toggleColor(color)} />
                                                </ZTooltip>
                                            ))}
                                    </Swatches>
                                    <HexRow>
                                        <input
                                            value={hex}
                                            placeholder='#RRGGBB'
                                            aria-label={T._('Another color', 'Admin')}
                                            onChange={(event) => setHex(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key !== 'Enter' || !isHex(hex)) return;
                                                if (!colors.includes(normalHex(hex))) toggleColor(normalHex(hex));
                                                setHex('');
                                            }}
                                        />
                                        <SmallButton
                                            type='button'
                                            disabled={!isHex(hex)}
                                            onClick={() => {
                                                if (!colors.includes(normalHex(hex))) toggleColor(normalHex(hex));
                                                setHex('');
                                            }}
                                        >
                                            {T._('Add', 'Admin')}
                                        </SmallButton>
                                    </HexRow>
                                </div>
                            </Row>
                        </>
                    )}
                    <Foot>
                        <span>{summary}</span>
                        <Make type='button' disabled={!ready} onClick={generate}>
                            <GenerateIcon />
                            {T._('Generate', 'Admin')}
                        </Make>
                    </Foot>
                </Panel>
            )}
        </PropertiesPanelLookProvider>
    );
};
